import type { SxProps, Theme } from '@mui/material';
import { foundationColors } from 'framework/ui/themes';

const heatmapCellStyles = (theme: Theme) => ({
  minHeight: 'var(--square-size)',
  fontSize: '9px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid',
  borderColor: theme.palette.border?.default,
  '&.today': {
    boxSizing: 'border-box',
    position: 'relative',
    '&::before': {
      boxSizing: 'border-box',
      content: "''",
      position: 'absolute',
      height: 'calc(100% + 10px)',
      width: 'calc(100% + 4px)',
      top: '-5px',
      left: '-2px',
      border: '2px solid',
      borderColor: theme.palette.primary.main,
      borderTopColor: 'transparent',
      borderBottomColor: 'transparent',
    },
  },
  '&[data-datetype="WORKING"]': {
    backgroundColor: theme.palette.common.white,
  },
  '&[data-datetype="HOLIDAY"], &[data-datetype="HOLIDAY"][data-state="NEUTRAL"], &[data-datetype="HOLIDAY"][data-state="POSITIVE"], &[data-datetype="HOLIDAY"][data-state="NEGATIVE"]':
    {
      backgroundColor: theme.palette.border?.default,
    },
  '&[data-datetype="LEAVE"], &[data-datetype="LEAVE"][data-state="NEUTRAL"], &[data-datetype="LEAVE"][data-state="POSITIVE"], &[data-datetype="LEAVE"][data-state="NEGATIVE"]':
    {
      backgroundColor: theme.palette.timeline?.planning,
    },
  '&[data-state="NEUTRAL"]': {
    backgroundColor: foundationColors.warningSoft,
  },
  '&[data-state="POSITIVE"]': {
    backgroundColor: theme.palette.timeline?.demo,
  },
  '&[data-state="NEGATIVE"]': {
    backgroundColor: foundationColors.errorStrongSurface,
  },
});

export const performanceHeatmapSx: SxProps<Theme> = theme => ({
  '--square-size': '30px',
  '--week-square-size': 'calc(var(--square-size) * 3 + var(--square-gap) * 2)',
  '--square-gap': '2px',
  '--week-square-gap': '9px',
  '--month-square-gap': '20px',
  '--week-width': 'calc(var(--square-size) + var(--square-gap))',
  '--labels-width': '200px',
  '--divider-size': '17px',
  '& .performance-heatmap': {
    flexShrink: 0,
    flexGrow: 1,
    maxWidth: 'calc(100% - var(--labels-width))',
    p: 0.25,
  },
  '& .performance-heatmap__scroll': {
    overflow: 'auto',
    contain: 'inline-size',
  },
  '& .performance-heatmap__inner': {
    display: 'grid',
    gridTemplateColumns: 'auto',
    gap: 'var(--square-gap)',
  },
  '& .performance-heatmap__months': {
    display: 'grid',
    minHeight: 'var(--square-size)',
    color: 'text.secondary',
    fontSize: '12px',
    '&:first-child': { alignItems: 'flex-end' },
    '& li': { whiteSpace: 'nowrap' },
  },
  '& .performance-heatmap__wrapper': {
    display: 'flex',
    '& ul': { margin: 0, padding: 0, listStyle: 'none' },
  },
  '& .performance-heatmap__wrapper > .performance-heatmap__labels': {
    width: 'var(--labels-width)',
    flexShrink: 0,
    display: 'grid',
    gap: 'var(--square-gap)',
    alignItems: 'center',
    marginTop: 'calc(var(--square-size) + var(--square-gap))',
    marginRight: '5px',
    '& .divider + .divider': { display: 'none' },
    '& li': {
      width: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'block',
      maxWidth: 'var(--labels-width)',
    },
  },
  '& .performance-heatmap__days': {
    display: 'grid',
    gap: 'var(--square-gap)',
    '&:nth-child(2) li.today::before': {
      top: '-2px',
      borderTopColor: theme.palette.primary.main,
    },
    '&:nth-last-child(2) li.today::before': {
      top: '-7px',
      borderBottomColor: theme.palette.primary.main,
    },
    '&:nth-child(2):nth-last-child(2) li.today::before': {
      top: '-2px',
      height: 'calc(100% + 4px)',
    },
    '&.divider li': {
      height: 'var(--divider-size)',
      minHeight: 'var(--divider-size)',
      backgroundColor: 'transparent !important',
      borderColor: 'transparent',
      color: 'transparent',
    },
    '&.divider + &.divider li': { display: 'none' },
    '&.performance-heatmap__days--weekly': { gap: 'var(--week-square-gap)' },
    '& li:has(.performance-heatmap__days)': { border: 0 },
    '& li > a': {
      display: 'block',
      width: '100%',
      height: '100%',
      minHeight: 'calc(var(--square-size) - 2px)',
      zIndex: 1,
    },
    '& .performance-heatmap__days': {
      width: '100%',
      gap: 0,
      '& li:not(:last-child)': { borderRightWidth: 0 },
      '& li.performance-heatmap__day--hidden': {
        background: `linear-gradient(315deg, transparent 49.9%, ${theme.palette.border?.default} 49.9%, ${theme.palette.border?.default} 60%, transparent 60%), linear-gradient(315deg, ${theme.palette.border?.default} 10%, transparent 10%)`,
        backgroundSize: '0.5em 0.5em',
        color: 'transparent',
      },
      '& li.performance-heatmap__day--empty': {
        backgroundColor: 'transparent',
        color: 'transparent',
      },
    },
  },
  '& .performance-heatmap__legend-cell, & .performance-heatmap__days li': heatmapCellStyles(theme),
  '& .performance-heatmap__legend': {
    display: 'flex',
    alignItems: 'flex-start',
    marginTop: '30px',
    flexWrap: 'wrap',
    '@media (min-width: 990px)': { marginLeft: 'var(--labels-width)' },
    '& .performance-heatmap__legend-inner--row': {
      '@media (min-width: 990px)': {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      },
    },
    '& .performance-heatmap__legend-item': {
      display: 'flex',
      alignItems: 'center',
      marginBottom: 'var(--square-gap)',
      marginRight: '30px',
      '& > div': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexDirection: 'row-reverse',
        minWidth: 'calc(var(--square-size) + var(--square-gap))',
        flexShrink: 0,
        marginRight: '7px',
      },
    },
    '& .performance-heatmap__legend-cell': {
      width: 'var(--square-size)',
      height: 'var(--square-size)',
      flexGrow: 0,
    },
  },
  '& .performance-heatmap__header': {
    marginBottom: '20px',
    '@media (min-width: 768px)': {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      marginBottom: '25px',
    },
    '& .performance-heatmap__header-controls > div:first-child, & .filter-item-wrapper': {
      margin: 0,
      width: '140px',
      maxWidth: '100%',
      '@media (max-width: 767px)': { marginTop: '15px' },
      '& .form-group': { margin: 0 },
      '& input:hover': { cursor: 'pointer' },
      '@media (min-width: 768px)': { '& .rdtPicker': { right: 0 } },
    },
    '& .filters-reset-btn': { display: 'none' },
  },
  '& .performance-heatmap__header-controls': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '& > div': { marginLeft: '10px' },
    '& > button': { display: 'none' },
    '& .form-group': { marginBottom: 0 },
    '& .filter-item-wrapper .form-control': {
      paddingTop: '5px',
      paddingBottom: '5px',
      fontSize: '0.875rem',
      lineHeight: 1.75,
      borderColor: 'text.secondary',
      '&:hover': { backgroundColor: 'action.hover' },
    },
    '& button': { fontWeight: 400 },
  },
  '& .performance-heatmap__pagination': {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    minHeight: '32px',
    paddingTop: '15px',
    '@media (min-width: 990px)': { marginLeft: 'calc(var(--labels-width) + 5px)' },
    '& .MuiPagination-root': {
      marginRight: '20px',
      '@media (min-width: 990px)': { marginLeft: '-10px' },
    },
  },
  '& .performance-heatmap__sticky-header': {
    '& .performance-heatmap-sticky-header__background': { display: 'none' },
    '&.sticky': {
      marginTop: '64px',
      zIndex: 2,
      '@media (max-width: 767px)': { marginTop: '60px' },
      '& .performance-heatmap-sticky-header__background, &::before': {
        position: 'absolute',
        top: 0,
        height: '36px',
        backgroundColor: 'background.paper',
        zIndex: 1,
      },
      '&::before': {
        content: "''",
        left: 'calc(0px - var(--labels-width) - 5px)',
        width: 'calc(var(--labels-width) + 5px)',
      },
      '& .performance-heatmap-sticky-header__background': {
        display: 'block',
        left: 0,
        right: 0,
      },
      '& .performance-heatmap-sticky-header__content': {
        overflow: 'hidden',
        position: 'relative',
        zIndex: 2,
      },
    },
  },
});

export const performanceHeatmapTooltipLegendSx: SxProps<Theme> = theme => ({
  '--square-size': '30px',
  '--square-gap': '2px',
  margin: 0,
  display: 'block',
  '& .performance-heatmap__legend-item': {
    display: 'flex',
    alignItems: 'center',
    marginRight: '3px',
    marginBottom: '4px',
    '&:last-child': { marginBottom: 0 },
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexDirection: 'row-reverse',
      minWidth: '21px',
      flexShrink: 0,
      marginRight: '7px',
    },
  },
  '& .performance-heatmap__legend-cell': {
    ...heatmapCellStyles(theme),
    width: '21px',
    height: '21px',
    minHeight: '21px',
    flexGrow: 0,
  },
});
